// Modelo de pantalla de Cohorts: condiciones unidas con Y sobre los datos del cliente. Un segmento
// junta cohorts con O; aquí solo se arma cada cohort.
import type {
    CohortConditionOutcome,
    CohortFactKey,
    CohortFactType,
    CohortFactValue,
    CohortOperator,
    CohortStatusWire,
    DefinitionLogEvent,
} from '@/api/services/loan-offerings/cohorts.contract';

export type CohortStatus = CohortStatusWire;

/** Una condición «dato operador valor» tal como la edita el armador. */
export interface CohortCondition {
    field: CohortFactKey;
    op: CohortOperator;
    /** Con los operadores de un solo valor. `null` mientras falte. */
    value: CohortFactValue | null;
    /** Solo con «es alguno de» y «no es ninguno de». */
    values: string[];
}

export interface CohortSegmentRef {
    segmentId: string;
    name: string;
}

/** Un cohort tal como lo lee la pantalla. */
export interface Cohort {
    cohortId: string;
    name: string;
    description: string;
    conditions: CohortCondition[];
    status: CohortStatus;
    version: number;
    /** `null` = sin calcular. */
    memberCount: number | null;
    pctTotal: number | null;
    countedAt: string | null;
    countPending: boolean;
    usedBySegments: CohortSegmentRef[];
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface CohortFieldOption {
    value: string;
    label: string;
}

/** Un dato del catálogo, con sus operadores y si tiene fuente. */
export interface CohortField {
    key: CohortFactKey;
    type: CohortFactType;
    label: string;
    unit: string | null;
    options: CohortFieldOption[];
    operators: CohortOperator[];
    hasSource: boolean;
    missingSourceReason: string | null;
}

/** Lo que el operador está editando: lo que el PUT puede parchar. */
export interface CohortDraft {
    name: string;
    description: string;
    conditions: CohortCondition[];
}

/** Qué cohorts lista la tabla. */
export type CohortListFilter = CohortStatus;

/** El resultado de una condición al probarla con un cliente. */
export interface CohortConditionResult {
    condition: CohortCondition;
    /** `null` = el cliente no tiene el dato. */
    actual: CohortFactValue | null;
    outcome: CohortConditionOutcome;
}

export interface CohortTestResult {
    retailCustomerId: string;
    matched: boolean;
    conditions: CohortConditionResult[];
}

export interface CohortLogEntry {
    event: DefinitionLogEvent;
    occurredAt: string;
    performedBy: string;
    reason: string | null;
    version: number;
}

/** Topes del contrato, para no descubrirlos al guardar. */
export const COHORT_NAME_MAX_LENGTH = 120;
export const COHORT_DESCRIPTION_MAX_LENGTH = 500;
export const MAX_COHORT_CONDITIONS = 10;

export const COHORT_STATUS_LABEL: Record<CohortStatus, string> = {
    ACTIVE: 'Activo',
    ARCHIVED: 'Archivado',
};

export const COHORT_FILTER_LABEL: Record<CohortListFilter, string> = {
    ACTIVE: 'Activos',
    ARCHIVED: 'Archivados',
};

/** Qué dice la marca de pendiente. */
export const PENDING_RECOUNT_LABEL = 'Pendiente de recálculo';
export const PENDING_RECOUNT_REASON =
    'Cambió después del último conteo. El número se actualiza en el próximo recálculo.';

export const NOT_COUNTED_LABEL = 'Sin calcular';
